import React, { useState, useRef, useEffect } from 'react';
import { 
  Palette, Droplets, Image, Video, Target, Zap, 
  Save, X, Plus, Unlock, Crown, File, Music, 
  FileText, Trash2, Upload, Folder, FileArchive,
  CheckCircle, AlertCircle, Edit2, Check, XCircle
} from 'lucide-react';
import { IoCloseCircleSharp } from 'react-icons/io5';

const LessonModal = ({
  showModal,
  setShowModal,
  formData,
  setFormData,
  handleInputChange,
  editingLesson,
  loading,
  handleSaveLesson,
  handleMaterialChange,
  addMaterialField,
  removeMaterialField,
  extractKinescopeId,
  createKinescopeEmbed,
  categories,
  levels,
  techniques
}) => {
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadErrors, setUploadErrors] = useState({});
  const [editingFileName, setEditingFileName] = useState(null);
  const [editedFileName, setEditedFileName] = useState('');
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const API_BASE_URL = 'https://artdownload2-production.up.railway.app';
  const folderName = 'lesson-files';
  const imageFolderName = 'lesson-images';

  // Поддерживаемые типы файлов
  const supportedFileTypes = {
    'application/pdf': { icon: <FileText className="w-5 h-5" />, color: 'text-red-500', label: 'PDF' },
    'audio/mpeg': { icon: <Music className="w-5 h-5" />, color: 'text-green-500', label: 'MP3' },
    'audio/wav': { icon: <Music className="w-5 h-5" />, color: 'text-green-500', label: 'WAV' },
    'audio/mp3': { icon: <Music className="w-5 h-5" />, color: 'text-green-500', label: 'MP3' },
    'image/jpeg': { icon: <Image className="w-5 h-5" />, color: 'text-blue-500', label: 'JPG' },
    'image/png': { icon: <Image className="w-5 h-5" />, color: 'text-blue-500', label: 'PNG' },
    'image/gif': { icon: <Image className="w-5 h-5" />, color: 'text-blue-500', label: 'GIF' },
    'application/zip': { icon: <FileArchive className="w-5 h-5" />, color: 'text-yellow-500', label: 'ZIP' },
    'application/x-rar-compressed': { icon: <FileArchive className="w-5 h-5" />, color: 'text-yellow-500', label: 'RAR' },
  };

  // Поддерживаемые типы изображений
  const supportedImageTypes = {
    'image/jpeg': { label: 'JPG' },
    'image/png': { label: 'PNG' },
    'image/gif': { label: 'GIF' },
    'image/webp': { label: 'WEBP' },
    'image/svg+xml': { label: 'SVG' },
  };

  // Получение иконки для типа файла
  const getFileIcon = (fileType) => {
    return supportedFileTypes[fileType] || { icon: <File className="w-5 h-5" />, color: 'text-gray-500', label: 'Файл' };
  };

  // Форматирование размера файла
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Функция для загрузки файла на сервер
  const uploadFileToServer = async (file, folder, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const xhr = new XMLHttpRequest();
    
    return new Promise((resolve, reject) => {
      xhr.open('POST', `${API_BASE_URL}/upload?folder=${folder}`);
      
      // Отслеживаем прогресс загрузки
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percentage = Math.round((event.loaded * 100) / event.total);
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percentage
          });
        }
      };
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success && response.filename) {
              const fullUrl = `${API_BASE_URL}${response.url}`;
              resolve({
                ...response,
                url: fullUrl
              });
            } else {
              reject(new Error(response.error || 'Неизвестная ошибка сервера'));
            }
          } catch (error) {
            reject(new Error('Некорректный JSON ответ'));
          }
        } else {
          reject(new Error(`Ошибка загрузки: ${xhr.status} ${xhr.statusText}`));
        }
      };
      
      xhr.onerror = () => {
        reject(new Error('Ошибка сети при загрузке файла'));
      };
      
      xhr.send(formData);
    });
  };

  // Обработка выбора файлов
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    const errors = {};

    files.forEach(file => {
      const fileType = file.type;
      const maxSize = 50 * 1024 * 1024; // 50MB (как на бэкенде)
      
      if (file.size > maxSize) {
        errors[file.name] = `Файл слишком большой. Максимальный размер: 50MB`;
        return;
      }
      
      // Проверяем тип файла
      const isSupported = Object.keys(supportedFileTypes).includes(fileType) || 
                         fileType.startsWith('image/') || 
                         fileType.startsWith('audio/') ||
                         fileType === 'application/pdf' ||
                         fileType === 'application/zip' ||
                         fileType === 'application/x-rar-compressed';
      
      if (!isSupported) {
        errors[file.name] = `Формат не поддерживается`;
        return;
      }
      
      validFiles.push(file);
    });
    
    if (Object.keys(errors).length > 0) {
      const errorMessage = Object.entries(errors)
        .map(([name, error]) => `${name}: ${error}`)
        .join('\n');
      alert(`Некоторые файлы не прошли проверку:\n${errorMessage}`);
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
    
    event.target.value = ''; // Сброс input
  };

  // Обработка выбора изображения
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const file = files[0];
    const fileType = file.type;
    const maxSize = 10 * 1024 * 1024; // 10MB для изображений
    
    // Проверяем тип файла
    if (!Object.keys(supportedImageTypes).includes(fileType)) {
      alert(`Формат изображения не поддерживается. Поддерживаемые форматы: ${Object.values(supportedImageTypes).map(i => i.label).join(', ')}`);
      event.target.value = '';
      return;
    }
    
    if (file.size > maxSize) {
      alert(`Изображение слишком большое. Максимальный размер: 10MB`);
      event.target.value = '';
      return;
    }

    // Загружаем изображение
    uploadImage(file);
    event.target.value = '';
  };

  // Загрузка изображения на сервер
  const uploadImage = async (file) => {
    setUploadingImage(true);
    
    try {
      const result = await uploadFileToServer(
        file, 
        imageFolderName, 
        (progress) => {
          // Можно добавить прогресс загрузки изображения если нужно
          console.log('Прогресс загрузки изображения:', progress.percentage);
        }
      );
      
      // Обновляем URL изображения в форме
      setFormData(prev => ({
        ...prev,
        image: result.url
      }));
      
      // Убрано оповещение об успешной загрузке изображения
    } catch (error) {
      console.error('Ошибка загрузки изображения:', error);
      alert(`Ошибка загрузки изображения: ${error.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  // Удаление файла из списка выбранных
  const removeSelectedFile = (index) => {
    const fileName = selectedFiles[index].name;
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fileName];
      return newErrors;
    });
  };

  // Удаление изображения урока
  const removeLessonImage = () => {
    if (!window.confirm('Удалить изображение урока?')) return;
    setFormData(prev => ({ ...prev, image: '' }));
  };

  // Загрузка файлов на сервер
  const uploadFiles = async () => {
    if (selectedFiles.length === 0) {
      alert('Выберите файлы для загрузки');
      return;
    }

    setUploadingFiles(true);
    setUploadErrors({});
    const uploadedFiles = [];
    const failedFiles = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileName = file.name;
      
      // Сбрасываем прогресс для этого файла
      setUploadProgress(prev => ({
        ...prev,
        [fileName]: { loaded: 0, total: file.size, percentage: 0 }
      }));

      try {
        const result = await uploadFileToServer(
          file,
          folderName,
          (progress) => {
            setUploadProgress(prev => ({
              ...prev,
              [fileName]: progress
            }));
          }
        );
        
        uploadedFiles.push({
          name: result.originalName || file.name,
          filename: result.filename,
          type: result.mimeType || file.type,
          size: result.size || file.size,
          url: result.url,
          uploadedAt: new Date().toISOString()
        });
        
        // Обновляем прогресс до 100%
        setUploadProgress(prev => ({
          ...prev,
          [fileName]: { loaded: file.size, total: file.size, percentage: 100 }
        }));
        
        console.log('Файл успешно загружен:', result);
        
      } catch (error) {
        console.error(`Ошибка загрузки файла ${fileName}:`, error);
        setUploadErrors(prev => ({
          ...prev,
          [fileName]: error.message
        }));
        failedFiles.push(fileName);
      }
    }

    // Обновляем список файлов в formData
    if (uploadedFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        files: [...(prev.files || []), ...uploadedFiles]
      }));
    }

    // Очищаем успешно загруженные файлы из выбранных
    const newSelectedFiles = selectedFiles.filter(file => 
      failedFiles.includes(file.name)
    );
    setSelectedFiles(newSelectedFiles);

    // Показываем результат (только для ошибок)
    if (failedFiles.length > 0) {
      alert(`Не удалось загрузить: ${failedFiles.length} файл(ов)\nПроверьте консоль для подробностей`);
    }

    setUploadingFiles(false);
  };

  // Удаление загруженного файла
  const removeUploadedFile = async (fileIndex) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот файл из списка?')) return;

    const file = formData.files[fileIndex];
    
    try {
      // Удаляем только из состояния, так как API может не поддерживать удаление
      const newFiles = formData.files.filter((_, index) => index !== fileIndex);
      setFormData(prev => ({ ...prev, files: newFiles }));
      
      // Убрано оповещение об удалении файла
    } catch (error) {
      console.error('Ошибка удаления файла:', error);
      alert('Ошибка при удалении файла');
    }
  };

  // Начать редактирование имени файла
  const startEditFileName = (fileIndex) => {
    const file = formData.files[fileIndex];
    setEditingFileName(fileIndex);
    setEditedFileName(file.name || '');
  };

  // Сохранить отредактированное имя файла
  const saveEditedFileName = (fileIndex) => {
    if (!editedFileName.trim()) {
      alert('Название файла не может быть пустым');
      return;
    }

    // Получаем расширение файла из оригинального имени
    const originalFile = formData.files[fileIndex];
    const originalName = originalFile.name || originalFile.filename || '';
    const extension = originalName.includes('.') 
      ? originalName.substring(originalName.lastIndexOf('.'))
      : '';
    
    // Добавляем расширение если его нет в новом имени
    let newFileName = editedFileName.trim();
    if (!newFileName.includes('.') && extension) {
      newFileName += extension;
    }

    // Обновляем имя файла в состоянии
    const updatedFiles = [...formData.files];
    updatedFiles[fileIndex] = {
      ...updatedFiles[fileIndex],
      name: newFileName
    };

    setFormData(prev => ({
      ...prev,
      files: updatedFiles
    }));

    setEditingFileName(null);
    setEditedFileName('');
  };

  // Отменить редактирование имени файла
  const cancelEditFileName = () => {
    setEditingFileName(null);
    setEditedFileName('');
  };

  // Обработка нажатия Enter при редактировании
  const handleEditKeyPress = (e, fileIndex) => {
    if (e.key === 'Enter') {
      saveEditedFileName(fileIndex);
    } else if (e.key === 'Escape') {
      cancelEditFileName();
    }
  };

  // Очистка всех выбранных файлов
  const clearSelectedFiles = () => {
    setSelectedFiles([]);
    setUploadProgress({});
    setUploadErrors({});
  };

  // ============ ИСПРАВЛЕНИЕ: Функция для сохранения с опциональным описанием ============
  const handleSaveWithOptionalDescription = () => {
    // Создаем копию данных урока
    const dataToSend = { ...formData };
    
    // Если описание пустое или состоит только из пробелов - устанавливаем пустую строку
    if (!dataToSend.description || dataToSend.description.trim() === '') {
      dataToSend.description = '';
    }
    
    // Проверяем только название
    if (!dataToSend.title) {
      alert("Пожалуйста, заполните обязательное поле: название");
      return;
    }
    
    // Вызываем родительскую функцию с подготовленными данными
    handleSaveLesson(dataToSend);
  };

  // Проверка перед сохранением урока
  const handleSaveWithFiles = async () => {
    if (selectedFiles.length > 0) {
      const shouldUpload = window.confirm(
        `У вас есть ${selectedFiles.length} незагруженных файлов. Хотите загрузить их перед сохранением урока?`
      );
      
      if (shouldUpload) {
        await uploadFiles();
        // После загрузки сразу сохраняем урок без дополнительного оповещения
        handleSaveWithOptionalDescription();
        return;
      } else {
        const saveAnyway = window.confirm(
          'Файлы не будут загружены. Продолжить сохранение урока без них?'
        );
        if (saveAnyway) {
          handleSaveWithOptionalDescription();
        }
        return;
      }
    }
    
    // Если нет незагруженных файлов, просто сохраняем
    handleSaveWithOptionalDescription();
  };

  // Сброс состояния редактирования при закрытии модального окна
  useEffect(() => {
    if (!showModal) {
      setEditingFileName(null);
      setEditedFileName('');
    }
  }, [showModal]);

  return (
    showModal && (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
        <div className="bg-gradient-to-b from-gray-900 to-black rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Заголовок */}
          <div className="sticky top-0 bg-gradient-to-b from-gray-900 to-gray-900/95 p-4 flex items-center justify-between border-b border-gray-800">
            <h2 className="text-xl font-bold text-white">
              {editingLesson ? 'Редактировать урок' : 'Новый урок'}
            </h2>
            <button 
              onClick={() => setShowModal(false)}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Форма */}
          <div className="p-4 space-y-6">
            {/* Техника */}
            <div>
              <label className="block text-white mb-2">Техника</label>
              <div className="flex gap-2">
                {techniques.map(tech => (
                  <button
                    key={tech}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, technique: tech }))}
                    className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                      formData.technique === tech
                        ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {tech === 'Акрил' ? <Palette className="inline w-4 h-4 mr-2" /> : <Droplets className="inline w-4 h-4 mr-2" />}
                    {tech}
                  </button>
                ))}
              </div>
            </div>

            {/* Заголовок */}
            <div>
              <label className="block text-white mb-2">Название урока *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Например: Портрет души"
                required
              />
            </div>

            {/* Описание */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-white">Описание</label>
                <span className="text-xs text-gray-400">Не обязательно</span>
              </div>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Краткое описание урока (необязательно)"
              />
            </div>

            {/* Изображение урока */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-white flex items-center">
                  <Image className="w-4 h-4 mr-2" />
                  Изображение урока
                </label>
                {formData.image && (
                  <button
                    type="button"
                    onClick={removeLessonImage}
                    className="text-sm text-red-400 hover:text-red-300 flex items-center"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Удалить
                  </button>
                )}
              </div>
              
              {/* Предпросмотр изображения */}
              {formData.image ? (
                <div className="mb-3">
                  <div className="relative w-full h-48 rounded-lg overflow-hidden mb-2">
                    <img 
                      src={formData.image} 
                      alt="Предпросмотр изображения урока"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-3">
                      <span className="text-xs text-white bg-black/50 px-2 py-1 rounded">
                        Текущее изображение
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">
                    URL: <span className="text-gray-300 truncate block">{formData.image}</span>
                  </p>
                </div>
              ) : (
                <div className="mb-3 p-4 border-2 border-dashed border-gray-700 rounded-lg text-center">
                  <Image className="w-12 h-12 mx-auto mb-2 text-gray-500" />
                  <p className="text-gray-400 text-sm">Изображение не выбрано</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Будет использовано изображение по умолчанию
                  </p>
                </div>
              )}
              
              {/* Кнопка выбора изображения */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className={`flex-1 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors ${
                    uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <>
                      <span className="mr-2 animate-pulse">⏳</span>
                      Загрузка...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {formData.image ? 'Заменить изображение' : 'Загрузить изображение'}
                    </>
                  )}
                </button>
                
                {/* Ручной ввод URL (опционально) */}
                <button
                  type="button"
                  onClick={() => {
                    const url = prompt('Введите URL изображения (оставьте пустым для изображения по умолчанию):', formData.image || '');
                    if (url !== null) {
                      setFormData(prev => ({ ...prev, image: url || '' }));
                    }
                  }}
                  className="px-4 py-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  URL
                </button>
              </div>
              
              {/* Скрытый input для выбора изображения */}
              <input
                type="file"
                ref={imageInputRef}
                onChange={handleImageSelect}
                accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
                className="hidden"
                disabled={uploadingImage}
              />
              
              <p className="text-xs text-gray-400 mt-2">
                Поддерживаемые форматы: JPG, PNG, GIF, WEBP, SVG (максимум 10MB)
              </p>
            </div>

            {/* Видео URL */}
            <div>
              <label className="block text-white mb-2 flex items-center">
                <Video className="w-4 h-4 mr-2" />
                Kinescope код вставки
              </label>
              <input
                type="text"
                name="videoUrl"
                value={formData.videoUrl}
                onChange={handleInputChange}
                className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder='<div style="position: relative; padding-top: 56.25%; width: 100%"><iframe src="https://kinescope.io/embed/ID_ВИДЕО" style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;"></iframe></div>'
              />
              <p className="text-xs text-gray-400 mt-1">
                Вставьте полный код вставки из Kinescope
              </p>
            </div>

            {/* Предпросмотр видео */}
            {formData.videoUrl && (
              <div className="mt-3">
                <label className="block text-white mb-2 text-sm">Предпросмотр:</label>
                <div 
                  className="bg-gray-800 rounded-lg p-4 text-sm text-gray-400 overflow-auto max-h-40"
                  dangerouslySetInnerHTML={{ 
                    __html: extractKinescopeId(formData.videoUrl) 
                      ? createKinescopeEmbed(extractKinescopeId(formData.videoUrl))
                      : formData.videoUrl 
                  }}
                />
                <p className="text-xs text-green-400 mt-1">
                  {extractKinescopeId(formData.videoUrl) 
                    ? '✓ Kinescope ID найден: ' + extractKinescopeId(formData.videoUrl)
                    : '⚠ Введите корректный код вставки Kinescope'}
                </p>
              </div>
            )}

            {/* Уровень */}
            <div>
              <label className="block text-white mb-2">Уровень</label>
              <select
                name="level"
                value={formData.level}
                onChange={handleInputChange}
                className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {levels.map(lvl => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </select>
            </div>

            {/* Материалы */}
            <div>
              <label className="block text-white mb-2">Материалы для урока</label>
              {formData.materials.map((material, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={material}
                    onChange={(e) => handleMaterialChange(index, e.target.value)}
                    className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Например: Акрил, Холст, Кисти"
                  />
                  <button
                    type="button"
                    onClick={() => removeMaterialField(index)}
                    className="bg-red-500/20 text-red-400 rounded-lg px-3 hover:bg-red-500/30 transition-colors"
                    disabled={formData.materials.length <= 1}
                  >
                    <IoCloseCircleSharp className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addMaterialField}
                className="text-purple-400 hover:text-purple-300 text-sm flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Добавить материал
              </button>
            </div>

            {/* Раздел для загрузки файлов */}
            <div className="border border-gray-700 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center">
                    <Folder className="w-5 h-5 mr-2" />
                    Дополнительные материалы
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Загрузите PDF инструкции, аудио-медитации, дополнительные изображения
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedFiles.length > 0 && (
                    <button
                      type="button"
                      onClick={clearSelectedFiles}
                      className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                      disabled={uploadingFiles || uploadingImage}
                    >
                      Очистить
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
                    disabled={uploadingFiles || uploadingImage}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Выбрать файлы
                  </button>
                </div>
              </div>

              {/* Скрытый input для выбора файлов */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                accept=".pdf,.mp3,.wav,.jpg,.jpeg,.png,.gif,.zip,.rar"
                className="hidden"
                disabled={uploadingFiles || uploadingImage}
              />

              {/* Выбранные файлы для загрузки */}
              {selectedFiles.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-white font-medium mb-2">
                    Выбрано файлов: {selectedFiles.length}
                    {uploadingFiles && ' (Идет загрузка...)'}
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                    {selectedFiles.map((file, index) => {
                      const fileIcon = getFileIcon(file.type);
                      const progress = uploadProgress[file.name];
                      const error = uploadErrors[file.name];
                      
                      return (
                        <div 
                          key={index} 
                          className={`p-3 rounded-lg transition-all ${
                            error 
                              ? 'bg-red-900/30 border border-red-700/50' 
                              : 'bg-gray-800/50 hover:bg-gray-800/70'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <div className={`p-2 rounded-lg ${fileIcon.color} bg-gray-900`}>
                                {fileIcon.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-white truncate max-w-xs">
                                    {file.name}
                                  </p>
                                  {progress?.percentage === 100 && (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  )}
                                </div>
                                <p className="text-xs text-gray-400">
                                  {formatFileSize(file.size)} • {fileIcon.label}
                                </p>
                                
                                {/* Прогресс загрузки */}
                                {progress && progress.percentage < 100 && !error && (
                                  <div className="mt-2">
                                    <div className="w-full bg-gray-700 rounded-full h-1.5">
                                      <div 
                                        className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                                        style={{ width: `${progress.percentage}%` }}
                                      ></div>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">
                                      {progress.percentage}%
                                    </p>
                                  </div>
                                )}
                                
                                {/* Ошибка */}
                                {error && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-400" />
                                    <p className="text-xs text-red-400">
                                      Ошибка: {error}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            {!uploadingFiles && !error && (
                              <button
                                type="button"
                                onClick={() => removeSelectedFile(index)}
                                className="p-1.5 text-red-400 hover:text-red-300 ml-2"
                                title="Удалить из списка"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Кнопка загрузки */}
                  {!uploadingFiles && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={uploadFiles}
                        disabled={selectedFiles.length === 0 || uploadingImage}
                        className={`w-full py-3 rounded-lg font-medium flex items-center justify-center transition-all ${
                          selectedFiles.length === 0 || uploadingImage
                            ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-600 to-emerald-500 text-white hover:opacity-90'
                        }`}
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        Загрузить выбранные файлы ({selectedFiles.length})
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Уже загруженные файлы */}
              {formData.files && formData.files.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-white font-medium mb-2">
                    Загруженные материалы ({formData.files.length})
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                    {formData.files.map((file, index) => {
                      const fileIcon = getFileIcon(file.type);
                      
                      return (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg hover:bg-gray-800/50 transition-colors group"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`p-2 rounded-lg ${fileIcon.color} bg-gray-900`}>
                              {fileIcon.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              {/* Режим редактирования имени файла */}
                              {editingFileName === index ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={editedFileName}
                                    onChange={(e) => setEditedFileName(e.target.value)}
                                    onKeyDown={(e) => handleEditKeyPress(e, index)}
                                    className="flex-1 bg-gray-700 text-white rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => saveEditedFileName(index)}
                                    className="p-1 text-green-400 hover:text-green-300"
                                    title="Сохранить"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={cancelEditFileName}
                                    className="p-1 text-red-400 hover:text-red-300"
                                    title="Отменить"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm font-medium text-white truncate max-w-xs">
                                    {file.name || file.filename}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    <span className="text-xs text-gray-400">
                                      {formatFileSize(file.size)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {fileIcon.label}
                                    </span>
                                    {file.uploadedAt && (
                                      <span className="text-xs text-gray-500">
                                        {new Date(file.uploadedAt).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {editingFileName !== index && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => startEditFileName(index)}
                                  className="p-1.5 text-yellow-400 hover:text-yellow-300"
                                  title="Редактировать название"
                                  disabled={uploadingFiles || uploadingImage}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <a
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 text-blue-400 hover:text-blue-300"
                                  title="Открыть файл"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Проверяем доступность файла перед открытием
                                    fetch(file.url, { method: 'HEAD' })
                                      .catch(() => {
                                        alert('Файл недоступен по указанному URL');
                                        e.preventDefault();
                                      });
                                  }}
                                >
                                  <File className="w-4 h-4" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => removeUploadedFile(index)}
                                  className="p-1.5 text-red-400 hover:text-red-300"
                                  title="Удалить файл из списка"
                                  disabled={uploadingFiles || uploadingImage}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Информация о поддерживаемых форматах */}
              <div className="mt-4 p-3 bg-gray-900/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-medium text-white">Поддерживаемые форматы:</h5>
                  <span className="text-xs text-gray-400">Макс. 50MB на файл</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(supportedFileTypes).map(([type, info]) => (
                    <div key={type} className="flex items-center gap-1">
                      <div className={info.color}>
                        {info.icon}
                      </div>
                      <span className="text-xs text-gray-400">{info.label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Файлы сохраняются на отдельном сервере и доступны по прямым ссылкам
                </p>
              </div>
            </div>

            {/* Премиум доступ */}
            <div className="flex items-center p-3 bg-gray-800/30 rounded-lg">
              <input
                type="checkbox"
                id="premium"
                name="premium"
                checked={formData.premium}
                onChange={handleInputChange}
                className="w-5 h-5 rounded bg-gray-800 border-gray-700 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-gray-900"
              />
              <label htmlFor="premium" className="ml-3 text-white flex items-center">
                {formData.premium ? (
                  <>
                    <Crown className="w-4 h-4 mr-2 fill-yellow-500" />
                    <span className="font-medium">Только для премиум пользователей</span>
                  </>
                ) : (
                  <>
                    <Unlock className="w-4 h-4 mr-2" />
                    <span className="font-medium">Бесплатный доступ</span>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Кнопки действий */}
          <div className="sticky bottom-0 bg-gradient-to-t from-gray-900 to-gray-900/95 p-4 border-t border-gray-800">
            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
                disabled={loading || uploadingFiles || uploadingImage}
              >
                Отмена
              </button>
              <button
                onClick={handleSaveWithFiles}
                disabled={loading || uploadingFiles || uploadingImage}
                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading || uploadingFiles || uploadingImage ? (
                  <>
                    <span className="mr-2 animate-pulse">⏳</span>
                    {uploadingFiles ? 'Загрузка...' : uploadingImage ? 'Загрузка изображения...' : 'Сохранение...'}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    {editingLesson ? 'Обновить урок' : 'Создать урок'}
                  </>
                )}
              </button>
            </div>
            {(selectedFiles.length > 0 || uploadingFiles || uploadingImage) && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                {uploadingFiles 
                  ? 'Идет загрузка файлов...' 
                  : uploadingImage
                  ? 'Идет загрузка изображения...'
                  : `Есть ${selectedFiles.length} незагруженных файлов`}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  );
};

export default LessonModal;